import _ from 'lodash';
import {ThunkDispatch} from 'redux-thunk';

import {ErrorWithData} from '../../../../libs/shared/helpers/src/types/customErrors';
import {parseDomainName} from '../../../../libs/shared/helpers/src/utilities/stringHelpers';
import {extractNonGenericAccountDomainsFromContactUids} from '../../../helpers/accountHelpers';
import {CancelError, CancelToken} from '../../../helpers/asyncCancelHelpers';
import {buildUrlToNavigateToCardInContactsManager} from '../../../helpers/contactsManagerHelpers';
import {addAccount} from '../../../helpers/models/cardAttributeUpdateHelpers';
import {isEnhancedPrivateCard, selectCardViaPriorityOrder} from '../../../helpers/models/cardHelpers';
import {computeConflictingMergeValues, hasMergeConflict} from '../../../helpers/models/cardMergeHelpers';
import {globalNamespace} from '../../../helpers/models/namespaceHelpers';
import {ActionWithPayload, batch} from '../../../helpers/reduxHelpers';
import {CardModel, CardTypesEnum, parseCards} from '../../../models/api/cardModel';
import {ContactModel, parseContactUid} from '../../../models/api/contactModel';
import {CustomFieldAttribute} from '../../../models/api/customFieldAttributeModel';
import {DomainModel, DomainValidityStatusesEnum} from '../../../models/api/domainModel';
import {ContactDetailsTabsEnum} from '../../../models/api/localPreferencesModel';
import {PreviewCardMergeModel} from '../../../models/api/previewCardMergeModel';
import {VisualContactModel} from '../../../models/api/visualContactModel';
import {findPanelSequence} from '../../../services/panels';
import {buildPanelCancelToken, findPanelCancelToken} from '../../../services/panelsCancellers';
import {AppAction} from '../../appActions';
import {AppState} from '../../appState';
import {ThunkAction, ThunkAsyncAction} from '../../appThunk';
import {fetchAccountsAsyncAction, validateDomainAsyncAction} from '../../data/accounts/accountsActions';
import {fetchCardNotesAsyncAction} from '../../data/cardNotes/cardNotesActions';
import {
  createCardActionAsync,
  CreateCardTemplate,
  deleteCardsAsyncAction,
  duplicateCardActionAsync,
  fetchCardAsyncAction,
  fetchCardConversationsAsyncAction,
  fetchCardsForContactAsyncAction,
  fetchContactConversationsAsyncAction,
  fetchPreviewCardMergeAsyncAction,
  mergeCardsAsyncAction,
  OverrideMergeValues,
  searchForCardsAsyncAction,
  showContactDetailsMergeErrorBannerAction,
  updateCardAttributeAsyncAction,
} from '../../data/cards/cardsActions';
import {getCustomFieldsForAccountsFlat} from '../../data/customFields/customFieldsSelectors';
import {updateDataAction} from '../../data/dataGroupsActions';
import {getCards, getContacts, getFlatCards} from '../../data/dataSelectors';
import {updateContactDetailsPreferredTabAction} from '../../data/localPreferences/localPreferencesActions';
import {getContactDetailsPreferredTab} from '../../data/localPreferences/localPreferencesSelectors';
import {getHasNewContactsManagerAccounts} from '../../featureFlags/featureFlagsSelectors';
import {navigateToLinkAction} from '../../stateless/navigationActions';
import {
  createAccountFormUpdateDomainsAction,
  createAccountFormUpdateNameAction,
} from '../accounts/createAccountForm/createAccountFormActions';
import {showContactDetailsErrorBannerAction} from '../app/banners/bannersActions';
import {ContactDetailsErrorTypesEnum} from '../app/banners/bannersState';
import {PanelCancelTokenId} from '../panelCancelTokenId';
import {removePanelAction, setPanelAction} from '../panelsActions';
import {getPanels, makeGetAppPanelIdFromPanel} from '../panelsSelectors';
import {panelIsContactDetailsPanel, panelIsContactsManagerPanel} from '../panelsState';
import {
  AppPanelId,
  ContactDetailsPanelId,
  makeContactDetailsPanelId,
  NewPanelId,
  PanelTypesEnum,
  ViewerPanelId,
} from '../panelStateBase';
import {makeGetContactCardPanelFromParentPanelId} from './contactCard/contactCardPanelSelectors';
import {ContactDetailsPanelLocationsEnum, ContactDetailsScrollSectionsEnum} from './contactDetailsPanelState';
import {
  ContactDetailsCreateAccountSourceActionsEnum,
  subStateIsAccountDetailsSubState,
  subStateIsCreateAccountSubState,
} from './contactDetailsPanelSubState';
import {getContactsManagerPanel} from './contactsManager/contactsManagerPanelSelectors';
import {tableStateIsContactsTableState} from './contactsManager/table/contactsManagerTableState';

/*
 * Type enum.
 */

export enum ContactDetailsPanelActionsEnum {
  FETCH_CONTACT_DETAILS_DATA = '@@panels/contacts/FETCH_CONTACT_DETAILS_DATA',
  FETCH_CONTACT_DETAILS_DATA_COMPLETED = '@@panels/contacts/FETCH_CONTACT_DETAILS_DATA_COMPLETED',
  FETCH_CONTACT_DETAILS_DATA_FAILED = '@@panels/contacts/FETCH_CONTACT_DETAILS_DATA_FAILED',
  FETCH_CONTACT_DETAILS_CARD_NOTES = '@@panels/contacts/FETCH_CONTACT_DETAILS_CARD_NOTES',
  FETCH_CONTACT_DETAILS_CARD_NOTES_COMPLETED = '@@panels/contacts/FETCH_CONTACT_DETAILS_CARD_NOTES_COMPLETED',
  FETCH_CONTACT_DETAILS_CARD_NOTES_FAILED = '@@panels/contacts/FETCH_CONTACT_DETAILS_CARD_NOTES_FAILED',
  FETCH_CONTACT_DETAILS_CONVERSATIONS = '@@panels/contacts/FETCH_CONTACT_DETAILS_CONVERSATIONS',
  FETCH_CONTACT_DETAILS_CONVERSATIONS_COMPLETED = '@@panels/contacts/FETCH_CONTACT_DETAILS_CONVERSATIONS_COMPLETED',
  FETCH_CONTACT_DETAILS_CONVERSATIONS_FAILED = '@@panels/contacts/FETCH_CONTACT_DETAILS_CONVERSATIONS_FAILED',
  UPDATE_SELECTED_CONTACT = '@@panels/contacts/UPDATE_SELECTED_CONTACT',
  CONTACT_DETAILS_MERGE_PREVIEW_STARTED = '@@panels/contacts/CONTACT_DETAILS_MERGE_PREVIEW_STARTED',
  CONTACT_DETAILS_MERGE_PREVIEW_COMPLETED = '@@panels/contacts/CONTACT_DETAILS_MERGE_PREVIEW_COMPLETED',
  CONTACT_DETAILS_MERGE_FAILED = '@@panels/contacts/CONTACT_DETAILS_MERGE_FAILED',
  CONTACT_DETAILS_MERGE_SUCCESS = '@@panels/contacts/CONTACT_DETAILS_MERGE_SUCCESS',
  CONTACT_DETAILS_CLEAR_MERGE = '@@panels/contacts/CONTACT_DETAILS_CLEAR_MERGE',
  CONTACT_DETAILS_CREATE_FAILED = '@@panels/contacts/CONTACT_DETAILS_CREATE_FAILED',
  ACCOUNT_DOMAIN_VALIDATED = '@@panels/contacts/ACCOUNT_DOMAIN_VALIDATED',
  OPEN_ACCOUNT_DETAILS = '@@panels/contacts/OPEN_ACCOUNT_DETAILS',
  OPEN_CONTACT_DETAILS = '@@panels/contacts/OPEN_CONTACT_DETAILS',
  OPEN_CREATE_ACCOUNT_FORM = '@@panels/contacts/OPEN_CREATE_ACCOUNT_FORM',
  SCROLL_TO_SECTION = '@@panels/contacts/SCROLL_TO_SECTION',
  SCROLLED_TO_SECTION = '@@panels/contacts/SCROLLED_TO_SECTION',
}

/*
 * Types.
 */

interface ContactDetailsPanelGenericPayload {
  panelId: ContactDetailsPanelId;
}

interface FetchContactDetailsDataPayload extends ContactDetailsPanelGenericPayload {
  selectedContactUid: string | undefined;
  cancelTokenId: PanelCancelTokenId;
  selectedCardId: number | undefined;
}

export interface FetchContactDetailsDataAction extends ActionWithPayload<FetchContactDetailsDataPayload> {
  type: ContactDetailsPanelActionsEnum.FETCH_CONTACT_DETAILS_DATA;
}

interface FetchContactDetailsDataCompletedPayload extends ContactDetailsPanelGenericPayload {
  selectedCardId: number | undefined;
  cardIds: Array<number>;
  accountId?: number;
  namespace?: string;
  selectedDomain?: DomainModel;
}

export interface FetchContactDetailsDataCompletedAction
  extends ActionWithPayload<FetchContactDetailsDataCompletedPayload> {
  type: ContactDetailsPanelActionsEnum.FETCH_CONTACT_DETAILS_DATA_COMPLETED;
}

interface UpdateContactDetailsPanelSelectedContactPayload extends ContactDetailsPanelGenericPayload {
  selectedContactUid: string;
}

export interface UpdateContactDetailsPanelSelectedContactAction
  extends ActionWithPayload<UpdateContactDetailsPanelSelectedContactPayload> {
  type: ContactDetailsPanelActionsEnum.UPDATE_SELECTED_CONTACT;
}

export interface FetchContactDetailsDataFailedAction
  extends ActionWithPayload<ContactDetailsPanelGenericPayload> {
  type: ContactDetailsPanelActionsEnum.FETCH_CONTACT_DETAILS_DATA_FAILED;
}

interface ContactDetailsMergePreviewStartedPayload extends ContactDetailsPanelGenericPayload {
  mergeMainCardId: number;
  mergeCardIds: Array<number>;
}

export interface ContactDetailsMergePreviewStartedAction
  extends ActionWithPayload<ContactDetailsMergePreviewStartedPayload> {
  type: ContactDetailsPanelActionsEnum.CONTACT_DETAILS_MERGE_PREVIEW_STARTED;
}

interface ContactDetailsMergePreviewCompletedPayload extends ContactDetailsPanelGenericPayload {
  previewCardMerge: PreviewCardMergeModel;
}

export interface ContactDetailsMergePreviewCompletedAction
  extends ActionWithPayload<ContactDetailsMergePreviewCompletedPayload> {
  type: ContactDetailsPanelActionsEnum.CONTACT_DETAILS_MERGE_PREVIEW_COMPLETED;
}

export interface ContactDetailsMergeFailedAction
  extends ActionWithPayload<ContactDetailsPanelGenericPayload> {
  type: ContactDetailsPanelActionsEnum.CONTACT_DETAILS_MERGE_FAILED;
}

interface ContactDetailsMergeSuccessPayload extends ContactDetailsPanelGenericPayload {
  mergedCardIds: ReadonlyArray<number>;
}

export interface ContactDetailsMergeSuccessAction
  extends ActionWithPayload<ContactDetailsMergeSuccessPayload> {
  type: ContactDetailsPanelActionsEnum.CONTACT_DETAILS_MERGE_SUCCESS;
}

export interface ContactDetailsClearMergeAction extends ActionWithPayload<ContactDetailsPanelGenericPayload> {
  type: ContactDetailsPanelActionsEnum.CONTACT_DETAILS_CLEAR_MERGE;
}

export interface ContactDetailsCreateFailedAction
  extends ActionWithPayload<ContactDetailsPanelGenericPayload> {
  type: ContactDetailsPanelActionsEnum.CONTACT_DETAILS_CREATE_FAILED;
}

export interface FetchContactDetailsCardNotesAction
  extends ActionWithPayload<ContactDetailsPanelGenericPayload> {
  type: ContactDetailsPanelActionsEnum.FETCH_CONTACT_DETAILS_CARD_NOTES;
}

interface FetchContactDetailsCardNotesCompletedActionPayload extends ContactDetailsPanelGenericPayload {
  noteIds: ReadonlyArray<number>;
}

export interface FetchContactDetailsCardNotesCompletedAction
  extends ActionWithPayload<FetchContactDetailsCardNotesCompletedActionPayload> {
  type: ContactDetailsPanelActionsEnum.FETCH_CONTACT_DETAILS_CARD_NOTES_COMPLETED;
}

export interface FetchContactDetailsCardNotesFailedAction
  extends ActionWithPayload<ContactDetailsPanelGenericPayload> {
  type: ContactDetailsPanelActionsEnum.FETCH_CONTACT_DETAILS_CARD_NOTES_FAILED;
}

export interface FetchContactDetailsConversationsAction
  extends ActionWithPayload<ContactDetailsPanelGenericPayload> {
  type: ContactDetailsPanelActionsEnum.FETCH_CONTACT_DETAILS_CONVERSATIONS;
}

interface FetchContactDetailsConversationsCompletedActionPayload extends ContactDetailsPanelGenericPayload {
  conversationIds: ReadonlyArray<number>;
  hasMoreConversations: boolean;
}

export interface FetchContactDetailsConversationsCompletedAction
  extends ActionWithPayload<FetchContactDetailsConversationsCompletedActionPayload> {
  type: ContactDetailsPanelActionsEnum.FETCH_CONTACT_DETAILS_CONVERSATIONS_COMPLETED;
}

export interface FetchContactDetailsConversationsFailedAction
  extends ActionWithPayload<ContactDetailsPanelGenericPayload> {
  type: ContactDetailsPanelActionsEnum.FETCH_CONTACT_DETAILS_CONVERSATIONS_FAILED;
}

interface OpenAccountDetailsActionPayload extends ContactDetailsPanelGenericPayload {
  accountId?: number;
}

export interface OpenAccountDetailsAction extends ActionWithPayload<OpenAccountDetailsActionPayload> {
  type: ContactDetailsPanelActionsEnum.OPEN_ACCOUNT_DETAILS;
}

export interface OpenContactDetailsAction extends ActionWithPayload<ContactDetailsPanelGenericPayload> {
  type: ContactDetailsPanelActionsEnum.OPEN_CONTACT_DETAILS;
}

interface OpenCreateAccountFormActionPayload extends ContactDetailsPanelGenericPayload {
  source: ContactDetailsCreateAccountSourceActionsEnum;
  cardId: number;
  customAttributes: ReadonlyArray<CustomFieldAttribute>;
}

export interface OpenCreateAccountFormAction extends ActionWithPayload<OpenCreateAccountFormActionPayload> {
  type: ContactDetailsPanelActionsEnum.OPEN_CREATE_ACCOUNT_FORM;
}

interface PopoutContactDetailsOptions {
  parentPanelId: NewPanelId;
  selectedContactUid?: string;
  selectedCardId?: number;
  popupWidth?: number;
  popupContactNamespace?: string;
  popupConversationId?: number;
  shouldFetchContact?: boolean;
  shouldHideHeaderTabs?: boolean;
  sectionToScrollTo?: ContactDetailsScrollSectionsEnum;
}

interface FetchContactDetailsDataCompletedOptions {
  panelId: ContactDetailsPanelId;
  cardIds: Array<number>;
  selectedCardId?: number;
  accountId?: number;
  namespace?: string;
  selectedDomain?: DomainModel;
}

interface AccountDomainValidatedActionPayload extends ContactDetailsPanelGenericPayload {
  domain: DomainModel;
}

export interface AccountDomainValidatedAction extends ActionWithPayload<AccountDomainValidatedActionPayload> {
  type: ContactDetailsPanelActionsEnum.ACCOUNT_DOMAIN_VALIDATED;
}

export interface ScrolledToSectionAction extends ActionWithPayload<ContactDetailsPanelGenericPayload> {
  type: ContactDetailsPanelActionsEnum.SCROLLED_TO_SECTION;
}

interface ScrollToSectionActionPayload extends ContactDetailsPanelGenericPayload {
  section: ContactDetailsScrollSectionsEnum;
}

export interface ScrollToSectionAction extends ActionWithPayload<ScrollToSectionActionPayload> {
  type: ContactDetailsPanelActionsEnum.SCROLL_TO_SECTION;
}

/*
 * Action creators.
 */

export function fetchContactDetailsData(
  panelId: ContactDetailsPanelId,
  selectedContactUid: string | undefined,
  cancelTokenId: PanelCancelTokenId,
  selectedCardId?: number,
): FetchContactDetailsDataAction {
  return {
    type: ContactDetailsPanelActionsEnum.FETCH_CONTACT_DETAILS_DATA,
    payload: {
      panelId,
      selectedContactUid,
      cancelTokenId,
      selectedCardId,
    },
  };
}

export function fetchContactDetailsDataCompleted({
  panelId,
  selectedCardId,
  cardIds,
  accountId,
  namespace,
  selectedDomain,
}: FetchContactDetailsDataCompletedOptions): FetchContactDetailsDataCompletedAction {
  return {
    type: ContactDetailsPanelActionsEnum.FETCH_CONTACT_DETAILS_DATA_COMPLETED,
    payload: {
      panelId,
      selectedCardId,
      cardIds,
      accountId,
      namespace,
      selectedDomain,
    },
  };
}

export function fetchContactDetailsDataFailed(
  panelId: ContactDetailsPanelId,
): FetchContactDetailsDataFailedAction {
  return {
    type: ContactDetailsPanelActionsEnum.FETCH_CONTACT_DETAILS_DATA_FAILED,
    payload: {
      panelId,
    },
  };
}

export function contactDetailsMergePreviewStarted(
  panelId: ContactDetailsPanelId,
  mergeMainCardId: number,
  mergeCardIds: Array<number>,
): ContactDetailsMergePreviewStartedAction {
  return {
    type: ContactDetailsPanelActionsEnum.CONTACT_DETAILS_MERGE_PREVIEW_STARTED,
    payload: {
      panelId,
      mergeMainCardId,
      mergeCardIds,
    },
  };
}

export function contactDetailsMergePreviewCompleted(
  panelId: ContactDetailsPanelId,
  previewCardMerge: PreviewCardMergeModel,
): ContactDetailsMergePreviewCompletedAction {
  return {
    type: ContactDetailsPanelActionsEnum.CONTACT_DETAILS_MERGE_PREVIEW_COMPLETED,
    payload: {
      panelId,
      previewCardMerge,
    },
  };
}

export function contactDetailsMergeFailed(panelId: ContactDetailsPanelId): ContactDetailsMergeFailedAction {
  return {
    type: ContactDetailsPanelActionsEnum.CONTACT_DETAILS_MERGE_FAILED,
    payload: {
      panelId,
    },
  };
}

export function contactDetailsMergeSuccess(
  panelId: ContactDetailsPanelId,
  mergedCardIds: ReadonlyArray<number>,
): ContactDetailsMergeSuccessAction {
  return {
    type: ContactDetailsPanelActionsEnum.CONTACT_DETAILS_MERGE_SUCCESS,
    payload: {
      panelId,
      mergedCardIds,
    },
  };
}

export function contactDetailsClearMerge(panelId: ContactDetailsPanelId): ContactDetailsClearMergeAction {
  return {
    type: ContactDetailsPanelActionsEnum.CONTACT_DETAILS_CLEAR_MERGE,
    payload: {
      panelId,
    },
  };
}

export function contactDetailsCreateFailed(panelId: ContactDetailsPanelId): ContactDetailsCreateFailedAction {
  return {
    type: ContactDetailsPanelActionsEnum.CONTACT_DETAILS_CREATE_FAILED,
    payload: {
      panelId,
    },
  };
}

export function updateContactDetailsPanelSelectedContact(
  panelId: ContactDetailsPanelId,
  selectedContactUid: string,
): UpdateContactDetailsPanelSelectedContactAction {
  return {
    type: ContactDetailsPanelActionsEnum.UPDATE_SELECTED_CONTACT,
    payload: {
      panelId,
      selectedContactUid,
    },
  };
}

export function fetchContactDetailsCardNotes(
  panelId: ContactDetailsPanelId,
): FetchContactDetailsCardNotesAction {
  return {
    type: ContactDetailsPanelActionsEnum.FETCH_CONTACT_DETAILS_CARD_NOTES,
    payload: {
      panelId,
    },
  };
}

export function fetchContactDetailsCardNotesCompleted(
  panelId: ContactDetailsPanelId,
  noteIds: ReadonlyArray<number>,
): FetchContactDetailsCardNotesCompletedAction {
  return {
    type: ContactDetailsPanelActionsEnum.FETCH_CONTACT_DETAILS_CARD_NOTES_COMPLETED,
    payload: {
      panelId,
      noteIds,
    },
  };
}

export function fetchContactDetailsCardNotesFailed(
  panelId: ContactDetailsPanelId,
): FetchContactDetailsCardNotesFailedAction {
  return {
    type: ContactDetailsPanelActionsEnum.FETCH_CONTACT_DETAILS_CARD_NOTES_FAILED,
    payload: {
      panelId,
    },
  };
}

export function fetchContactDetailsConversations(
  panelId: ContactDetailsPanelId,
): FetchContactDetailsConversationsAction {
  return {
    type: ContactDetailsPanelActionsEnum.FETCH_CONTACT_DETAILS_CONVERSATIONS,
    payload: {
      panelId,
    },
  };
}

export function fetchContactDetailsConversationsCompleted(
  panelId: ContactDetailsPanelId,
  conversationIds: ReadonlyArray<number>,
  hasMoreConversations: boolean,
): FetchContactDetailsConversationsCompletedAction {
  return {
    type: ContactDetailsPanelActionsEnum.FETCH_CONTACT_DETAILS_CONVERSATIONS_COMPLETED,
    payload: {
      panelId,
      conversationIds,
      hasMoreConversations,
    },
  };
}

export function fetchContactDetailsConversationsFailed(
  panelId: ContactDetailsPanelId,
): FetchContactDetailsConversationsFailedAction {
  return {
    type: ContactDetailsPanelActionsEnum.FETCH_CONTACT_DETAILS_CONVERSATIONS_FAILED,
    payload: {
      panelId,
    },
  };
}

export function openAccountDetails(
  panelId: ContactDetailsPanelId,
  accountId?: number,
): OpenAccountDetailsAction {
  return {
    type: ContactDetailsPanelActionsEnum.OPEN_ACCOUNT_DETAILS,
    payload: {
      panelId,
      accountId,
    },
  };
}

export function openContactDetails(panelId: ContactDetailsPanelId): OpenContactDetailsAction {
  return {
    type: ContactDetailsPanelActionsEnum.OPEN_CONTACT_DETAILS,
    payload: {
      panelId,
    },
  };
}

export function openCreateAccountForm(
  panelId: ContactDetailsPanelId,
  source: ContactDetailsCreateAccountSourceActionsEnum,
  cardId: number,
  customAttributes: ReadonlyArray<CustomFieldAttribute>,
): OpenCreateAccountFormAction {
  return {
    type: ContactDetailsPanelActionsEnum.OPEN_CREATE_ACCOUNT_FORM,
    payload: {
      panelId,
      source,
      cardId,
      customAttributes,
    },
  };
}

export function accountDomainValidatedAction(
  panelId: ContactDetailsPanelId,
  domain: DomainModel,
): AccountDomainValidatedAction {
  return {
    type: ContactDetailsPanelActionsEnum.ACCOUNT_DOMAIN_VALIDATED,
    payload: {
      panelId,
      domain,
    },
  };
}

export function contactDetailsScrollToSectionAction(
  panelId: ContactDetailsPanelId,
  section: ContactDetailsScrollSectionsEnum,
): ScrollToSectionAction {
  return {
    type: ContactDetailsPanelActionsEnum.SCROLL_TO_SECTION,
    payload: {
      panelId,
      section,
    },
  };
}

export function contactDetailsScrolledToSectionAction(
  panelId: ContactDetailsPanelId,
): ScrolledToSectionAction {
  return {
    type: ContactDetailsPanelActionsEnum.SCROLLED_TO_SECTION,
    payload: {
      panelId,
    },
  };
}

/*
 * Actions.
 */

export function popoutContactDetailsAction(options: PopoutContactDetailsOptions): ThunkAction {
  return async (dispatch, getState) => {
    const {
      parentPanelId,
      selectedCardId,
      popupWidth,
      selectedContactUid,
      popupContactNamespace,
      popupConversationId,
      shouldFetchContact,
      shouldHideHeaderTabs,
      sectionToScrollTo,
    } = options;
    const popupPanelId = makeContactDetailsPanelId();
    const popupAppPanelId = makeGetAppPanelIdFromPanel()(getState(), {panelId: parentPanelId});
    const [cancelTokenId] = buildPanelCancelToken(popupPanelId);

    if (shouldFetchContact && selectedContactUid) {
      await dispatch(
        searchForAdditionalContactInformationAsyncAction(undefined, parseContactUid(selectedContactUid)[1]),
      );
    }

    dispatch(
      setPanelAction({
        id: popupPanelId,
        type: PanelTypesEnum.CONTACT_DETAILS,
        status: undefined,
        location: ContactDetailsPanelLocationsEnum.POPOUT,
        notesStatus: undefined,
        conversationStatus: undefined,
        cancelTokenId,
        popupAppPanelId,
        popupWidth,
        popupContactNamespace,
        popupConversationId,
        sequence: findPanelSequence(),
        selectedContactUid,
        selectedCardId,
        cardIds: [],
        noteIds: undefined,
        conversationIds: [],
        hasMoreConversations: false,
        shouldHideHeaderTabs,
        sectionToScrollTo,
      }),
    );
    // TODO: Add tracking around here for popout viewer
  };
}

export function navigateContactDetailsAction(
  panelId: ContactDetailsPanelId,
  recipientContactUid: string | undefined,
  cardId: number | undefined,
  location: ContactDetailsPanelLocationsEnum,
  viewerPanelId?: ViewerPanelId,
  popupAppPanelId?: AppPanelId,
): ThunkAction {
  return (dispatch, getState) => {
    const [cancelTokenId] = buildPanelCancelToken(panelId);

    // Check if we have a contact card open, this means we should select the open contact card value.
    // The contact card can only be open still if they clicked "Open details" from the card.
    const contactCard = makeGetContactCardPanelFromParentPanelId()(getState(), {
      parentPanelId: viewerPanelId,
    });
    const selectedContactUid = contactCard?.selectedContactUid ?? recipientContactUid;
    const selectedCardId = contactCard?.selectedContactUid ? contactCard?.selectedCardId : cardId;

    dispatch(
      setPanelAction({
        id: panelId,
        type: PanelTypesEnum.CONTACT_DETAILS,
        location,
        status: undefined,
        notesStatus: undefined,
        conversationStatus: undefined,
        cancelTokenId,
        popupAppPanelId,
        sequence: findPanelSequence(),
        selectedContactUid,
        selectedCardId,
        cardIds: [],
        noteIds: undefined,
        conversationIds: undefined,
        hasMoreConversations: undefined,
        shouldHideHeaderTabs: location === ContactDetailsPanelLocationsEnum.CONTACTS_MANAGER,
      }),
    );
  };
}

export function updateContactDetailsPanelSelectedContactAction(
  panelId: ContactDetailsPanelId,
  recipientContactUid: string,
): ThunkAction {
  return (dispatch, getState) => {
    const panel = getPanels(getState())[panelId];
    if (!panelIsContactDetailsPanel(panel)) {
      return;
    }

    // If we are trying to change to the currently selected contact, noop
    if (panel.selectedContactUid === recipientContactUid) {
      return;
    }

    dispatch(updateContactDetailsPanelSelectedContact(panelId, recipientContactUid));
  };
}

// Make fetch requests for additional data that we need when loading the panel for a given contact
export function fetchContactDetailsDataAction(
  panelId: ContactDetailsPanelId,
  contactUid: string | undefined,
  selectedCardId?: number,
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const state = getState();
    const panel = getPanels(getState())[panelId];
    if (!panelIsContactDetailsPanel(panel)) {
      return;
    }

    if (!contactUid) {
      return;
    }

    const contacts = getContacts(state);
    const contact = contacts[contactUid];
    const searchedContact = await dispatch(
      searchForAdditionalContactInformationAsyncAction(
        contact,
        contact?.handle ?? parseContactUid(contactUid)[1],
      ),
    );

    const selectedContact = searchedContact ?? contact;
    // If we fail to find a contact, we should mark the panel as loaded so we can show a fake card
    // to the end user.
    if (!selectedContact) {
      dispatch(fetchContactDetailsDataCompleted({panelId, cardIds: []}));
      return;
    }

    const cancelToken = findPanelCancelToken(panel.cancelTokenId);
    try {
      // Update the async status to loading to show a loading state
      dispatch(fetchContactDetailsData(panelId, contactUid, panel.cancelTokenId));

      // Make the request for the data
      const cards = await dispatch(fetchCardsForContactAsyncAction(selectedContact, {cancelToken}));

      // Update the async status to loaded and store the results of the fetch requests
      const initialSelectedCard =
        cards.find((card) => card.id === selectedCardId) ?? selectCardViaPriorityOrder(cards);

      const cardIds = cards.map((card) => card.id);
      dispatch(
        fetchContactDetailsDataCompleted({
          panelId,
          selectedCardId: initialSelectedCard?.id,
          cardIds,
          accountId: initialSelectedCard?.accountId,
          namespace: initialSelectedCard?.namespace,
          selectedDomain: extractAccountDomainFromCard(contactUid, initialSelectedCard),
        }),
      );

      dispatch(maybeSelectAccountTabForCardAction(panelId, initialSelectedCard));
    } catch (error) {
      dispatch(fetchContactDetailsDataFailed(panelId));
    }
  };
}

export function fetchContactDetailsDataContactsManagerAction(
  panelId: ContactDetailsPanelId,
  selectedCardId: number,
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const state = getState();
    const panel = getPanels(getState())[panelId];
    if (!panelIsContactDetailsPanel(panel)) {
      return;
    }

    const card = await dispatch(getOrFetchCardAsyncAction(selectedCardId));
    if (!card) {
      return;
    }

    const contactUid = card.contactUids[0];

    // Update the async status to loading to show a loading state
    dispatch(fetchContactDetailsData(panelId, contactUid, panel.cancelTokenId, selectedCardId));

    const contacts = getContacts(state);
    const contact = contacts[contactUid];
    const contactUidHandle = contactUid ? parseContactUid(contactUid)[1] : undefined;
    const searchedContact = await dispatch(
      searchForAdditionalContactInformationAsyncAction(contact, contact?.handle ?? contactUidHandle),
    );

    const selectedContact = searchedContact ?? contact;
    const cancelToken = findPanelCancelToken(panel.cancelTokenId);

    try {
      // If we have a selected contact, fetch to look for any duplicates.
      if (selectedContact) {
        // Make the request for the data
        const searchCards = await dispatch(fetchCardsForContactAsyncAction(selectedContact, {cancelToken}));
        const initialSelectedCard = searchCards.find((c) => c.id === selectedCardId);
        const cardIds = searchCards.map((c) => c.id);
        dispatch(
          fetchContactDetailsDataCompleted({panelId, selectedCardId: initialSelectedCard?.id, cardIds}),
        );
        dispatch(maybeSelectAccountTabForCardAction(panelId, initialSelectedCard));
      } else {
        const cardFromState = getCards(getState())[selectedCardId];
        if (!cardFromState) {
          await dispatch(fetchCardAsyncAction(selectedCardId));
        }
        dispatch(fetchContactDetailsDataCompleted({panelId, selectedCardId, cardIds: [selectedCardId]}));
        dispatch(maybeSelectAccountTabForCardAction(panelId, cardFromState));
      }
    } catch (error) {
      dispatch(fetchContactDetailsDataFailed(panelId));
    }
  };
}

function maybeSelectAccountTabForCardAction(panelId: ContactDetailsPanelId, card?: CardModel): ThunkAction {
  return (dispatch, getState) => {
    const panel = getPanels(getState())[panelId];
    if (!panelIsContactDetailsPanel(panel)) {
      return;
    }

    const preferredTab = getContactDetailsPreferredTab(getState());
    // We should not select the account tab or the card is private or we should no show header tabs.
    if (
      preferredTab !== ContactDetailsTabsEnum.ACCOUNT ||
      (card && isEnhancedPrivateCard(card)) ||
      panel.shouldHideHeaderTabs
    ) {
      return;
    }

    dispatch(contactDetailsOpenAccountDetailsAction(panelId, card?.accountId));
  };
}

function searchForAdditionalContactInformationAsyncAction(
  contact: ContactModel | undefined,
  handle: string | undefined,
): ThunkAsyncAction<ContactModel | undefined> {
  return async (dispatch) => {
    if ((contact && contact.url) || !handle) {
      return undefined;
    }

    const searchResult = await dispatch(searchForCardsAsyncAction(handle));
    const foundContact = searchResult?.contacts.find((c) => c.handle === handle);
    if (foundContact) {
      dispatch(updateDataAction({contacts: [foundContact]}));
    }
    return foundContact;
  };
}

function mergeWithExistingGlobalContactsAsyncAction(
  panelId: ContactDetailsPanelId,
  privateCard: CardModel,
  globalCards: ReadonlyArray<CardModel>,
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const panel = getPanels(getState())[panelId];
    if (!panelIsContactDetailsPanel(panel)) {
      return;
    }

    const globalCardIds = globalCards.map((c) => c.id);
    const mergeCardIds = [privateCard.id, ...globalCardIds];

    const previewCardMerge = await dispatch(fetchPreviewCardMergeAsyncAction(globalCardIds[0], mergeCardIds));

    if (hasMergeConflict(computeConflictingMergeValues(previewCardMerge, [privateCard, ...globalCards]))) {
      // Kickoff preview merge flow to let the user manually resolve conflicts.
      dispatch(previewMergeAction(panelId, globalCardIds[0], mergeCardIds, previewCardMerge));
    }
    // Auto-merge private card with global cards.
    else {
      dispatch(mergeAndFetchContactDetailCardsAction(panelId, globalCardIds[0], mergeCardIds));
    }
  };
}

export function convertToGlobalContactAsyncAction(
  panelId: ContactDetailsPanelId,
  card: CardModel,
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const panel = getPanels(getState())[panelId];
    if (!panelIsContactDetailsPanel(panel)) {
      return;
    }

    try {
      // Try duplicate the card to global namespace.
      const duplicatedCard = await dispatch(duplicateCardActionAsync(card, {namespace: globalNamespace}));
      if (!duplicatedCard) {
        throw Error(`Unable to duplicate card with cardId: ${card.id}`);
      }

      // Delete private card if duplication was successful.
      await dispatch(deleteCardsAsyncAction([card.id]));

      const contactsManagerPanel = getContactsManagerPanel(getState());
      if (
        panelIsContactsManagerPanel(contactsManagerPanel) &&
        tableStateIsContactsTableState(contactsManagerPanel.tableState)
      ) {
        // Redirect to converted global card if user is on contacts manager.
        dispatch(navigateToLinkAction(buildUrlToNavigateToCardInContactsManager(duplicatedCard)));
      } else {
        dispatch(
          fetchContactDetailsDataCompleted({
            panelId,
            selectedCardId: duplicatedCard.id,
            cardIds: panel.cardIds,
          }),
        );
      }
    } catch (error) {
      if (!(error instanceof ErrorWithData)) {
        dispatch(
          showContactDetailsErrorBannerAction(
            panelId,
            ContactDetailsErrorTypesEnum.CONVERT_PRIVATE_CONTACT_FAILED,
          ),
        );
        throw error;
      }

      // If there are already global cards with conflicting contacts, merge with global cards.
      dispatch(mergeWithExistingGlobalContactsAsyncAction(panelId, card, parseCards(error.data)));
    }
  };
}

export function previewMergeAction(
  panelId: ContactDetailsPanelId,
  mainCardId: number,
  mergeCardIds: Array<number>,
  preFetchPreviewCardMerge?: PreviewCardMergeModel,
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    try {
      dispatch(contactDetailsMergePreviewStarted(panelId, mainCardId, mergeCardIds));

      const previewCardMerge =
        preFetchPreviewCardMerge ||
        (await dispatch(fetchPreviewCardMergeAsyncAction(mainCardId, mergeCardIds)));
      if (!previewCardMerge) {
        return;
      }

      // We may need to fetch accounts if we do not have them in the redux store.
      if (getHasNewContactsManagerAccounts(getState())) {
        const accountIds = _(getFlatCards(getState()))
          .map((card) => card.accountId)
          .compact()
          .uniq()
          .value();
        await dispatch(fetchAccountsAsyncAction(accountIds));
      }

      dispatch(contactDetailsMergePreviewCompleted(panelId, previewCardMerge));
    } catch (error) {
      dispatch(contactDetailsClearMerge(panelId));
      throw error;
    }
  };
}

export function mergeAndFetchContactDetailCardsAction(
  panelId: ContactDetailsPanelId,
  mainCardId: number,
  mergeCardIds: ReadonlyArray<number>,
  shouldRequireRefetch?: boolean,
  overrides?: OverrideMergeValues,
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const panel = getPanels(getState())[panelId];
    if (!panelIsContactDetailsPanel(panel)) {
      return;
    }

    const {selectedContactUid, cancelTokenId} = panel;
    if (!selectedContactUid) {
      return;
    }

    const cancelToken = findPanelCancelToken(cancelTokenId);
    try {
      // TODO: Propagate the cancel token into mergeCardsAsyncAction()?
      cancelToken.throwIfRequested();

      // Update the async status to loading to show a loading state
      dispatch(fetchContactDetailsData(panelId, selectedContactUid, cancelTokenId));

      // Merge the passed in cards and update the main card in the redux store
      const card = await dispatch(mergeCardsAsyncAction(mainCardId, mergeCardIds, overrides));
      // If no card, return early. This should throw an error
      if (!card) {
        return;
      }
      // We have successfully merged the card, update store and dispatch merge successful
      batch(() => {
        dispatch(updateDataAction({cards: [card]}));
        dispatch(contactDetailsMergeSuccess(panelId, mergeCardIds));
      });

      const contactsManagerPanel = getContactsManagerPanel(getState());

      if (
        panelIsContactsManagerPanel(contactsManagerPanel) &&
        tableStateIsContactsTableState(contactsManagerPanel.tableState)
      ) {
        dispatch(navigateToLinkAction(buildUrlToNavigateToCardInContactsManager(card)));
      }
      // If we require a refetch (new base card), we should issue that request and return early
      if (shouldRequireRefetch) {
        dispatch(fetchContactDetailsDataAction(panel.id, panel.selectedContactUid));
        return;
      }
      dispatch(
        fetchContactDetailsDataCompleted({
          panelId,
          selectedCardId: card.id,
          cardIds: panel.cardIds.filter((cardId) => !mergeCardIds.includes(cardId)),
        }),
      );
    } catch (error) {
      if (error instanceof CancelError) {
        return;
      }

      dispatch(contactDetailsMergeFailed(panelId));
      throw error;
    }
  };
}

export function clearMergeAction(panelId: ContactDetailsPanelId): ThunkAsyncAction {
  return async (dispatch) => {
    dispatch(contactDetailsClearMerge(panelId));
  };
}

export function updateContactDetailsSelectedDuplicateCardAction(
  panelId: ContactDetailsPanelId,
  cardId: number,
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const panel = getPanels(getState())[panelId];
    if (!panelIsContactDetailsPanel(panel)) {
      return;
    }

    // Fetch and make sure card exists in the store
    const card = getState().data.cards[cardId];
    if (!card) {
      return;
    }

    dispatch(fetchContactDetailsDataCompleted({panelId, selectedCardId: card.id, cardIds: panel.cardIds}));
  };
}

export function createCardAndUpdateContactDetailsPanelAction(
  panelId: ContactDetailsPanelId,
  createTemplate: CreateCardTemplate,
  shouldHideLoadingState?: boolean,
): ThunkAsyncAction<CardModel | undefined> {
  return async (dispatch, getState) => {
    const panel = getPanels(getState())[panelId];
    if (!panelIsContactDetailsPanel(panel)) {
      return undefined;
    }

    const {selectedContactUid, cancelTokenId} = panel;
    if (!selectedContactUid) {
      return undefined;
    }

    const cancelToken = findPanelCancelToken(cancelTokenId);
    try {
      // TODO: Propagate the cancel token into createCardActionAsync()?
      cancelToken.throwIfRequested();

      if (!shouldHideLoadingState) {
        // Update the async status to loading to show a loading state
        dispatch(fetchContactDetailsData(panelId, selectedContactUid, panel.cancelTokenId));
      }

      const card = await dispatch(createCardActionAsync(createTemplate));
      dispatch(updateDataAction({cards: [card]}));

      dispatch(fetchContactDetailsDataCompleted({panelId, selectedCardId: card.id, cardIds: [card.id]}));
      return card;
    } catch (error) {
      if (error instanceof CancelError) {
        return undefined;
      }

      batch(() => {
        dispatch(
          showContactDetailsMergeErrorBannerAction(panelId, ContactDetailsErrorTypesEnum.CREATION_FAILED),
        );
        dispatch(contactDetailsCreateFailed(panelId));
      });
      return undefined;
    }
  };
}

export function fetchContactDetailsCardNotesAction(
  panelId: ContactDetailsPanelId,
  shouldSkipLoadingNotes?: boolean,
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const state = getState();
    const panel = getPanels(getState())[panelId];
    if (!panelIsContactDetailsPanel(panel)) {
      return;
    }

    if (!panel.selectedCardId) {
      return;
    }
    const card = getCards(state)[panel.selectedCardId];
    if (!card) {
      return;
    }

    const cancelToken = findPanelCancelToken(panel.cancelTokenId);
    try {
      // Removes any previous note ids that may be loaded
      dispatch(fetchContactDetailsCardNotes(panelId));

      // Fetch the notes
      const notes = shouldSkipLoadingNotes
        ? []
        : await dispatch(fetchCardNotesAsyncAction(card, {cancelToken}));

      // Update the store
      dispatch(
        fetchContactDetailsCardNotesCompleted(
          panelId,
          notes.map((note) => note.id),
        ),
      );
    } catch (error) {
      dispatch(fetchContactDetailsCardNotesFailed(panelId));
    }
  };
}

export function fetchContactDetailsConversationsAction(
  panelId: ContactDetailsPanelId,
  contact: ContactModel,
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const state = getState();
    const panel = getPanels(getState())[panelId];
    if (!panelIsContactDetailsPanel(panel)) {
      return;
    }

    const card = panel.selectedCardId ? getCards(state)[panel.selectedCardId] : undefined;

    const cancelToken = findPanelCancelToken(panel.cancelTokenId);
    try {
      // Removes any previous conversation information that may be loaded
      dispatch(fetchContactDetailsConversations(panelId));

      // Fetch the conversations
      const {conversationIds, hasMoreConversations} = await fetchConversations(
        cancelToken,
        dispatch,
        card,
        contact,
      );

      // Update the store
      dispatch(fetchContactDetailsConversationsCompleted(panelId, conversationIds, hasMoreConversations));
    } catch (error) {
      dispatch(fetchContactDetailsConversationsFailed(panelId));
    }
  };
}

async function fetchConversations(
  cancelToken: CancelToken,
  dispatch: ThunkDispatch<AppState, void, AppAction>,
  card?: CardModel | undefined,
  contact?: ContactModel | undefined,
) {
  if (!card && contact) {
    const contactConversations = await dispatch(fetchContactConversationsAsyncAction(contact, {cancelToken}));
    return {
      conversationIds: (contactConversations.conversations ?? []).map((conversation) => conversation.id),
      hasMoreConversations: contactConversations.hasMore,
    };
  }

  if (!card) {
    return {
      conversationIds: [],
      hasMoreConversations: false,
    };
  }

  const cardConversations = await dispatch(fetchCardConversationsAsyncAction(card, {cancelToken}));
  return {
    conversationIds: (cardConversations.conversations ?? []).map((conversation) => conversation.id),
    hasMoreConversations: cardConversations.hasMore,
  };
}

export function contactDetailsOpenAccountDetailsAction(
  panelId: ContactDetailsPanelId,
  accountId?: number,
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const panel = getPanels(getState())[panelId];
    if (!panelIsContactDetailsPanel(panel)) {
      return;
    }

    // We already have account details open
    if (
      panel.subState &&
      subStateIsAccountDetailsSubState(panel.subState) &&
      panel.subState.accountId === accountId
    ) {
      return;
    }

    batch(() => {
      dispatch(updateContactDetailsPreferredTabAction(ContactDetailsTabsEnum.ACCOUNT));
      dispatch(openAccountDetails(panelId, accountId));
    });
  };
}

export function contactDetailsOpenContactDetailsAction(panelId: ContactDetailsPanelId): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const panel = getPanels(getState())[panelId];
    if (!panelIsContactDetailsPanel(panel)) {
      return;
    }

    // We already have contact details open
    if (!panel.subState) {
      return;
    }

    batch(() => {
      dispatch(updateContactDetailsPreferredTabAction(ContactDetailsTabsEnum.CONTACT));
      dispatch(openContactDetails(panelId));
    });
  };
}

export function contactDetailsCreateAccountFormAction(
  panelId: ContactDetailsPanelId,
  source: ContactDetailsCreateAccountSourceActionsEnum,
  cardId: number,
  name?: string,
  domain?: string,
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const panel = getPanels(getState())[panelId];
    if (!panelIsContactDetailsPanel(panel)) {
      return;
    }

    // We already have the create account form
    if (panel.subState && subStateIsCreateAccountSubState(panel.subState)) {
      return;
    }

    // Fetch all the custom fields and add a default value.
    const accountsCustomFields = getCustomFieldsForAccountsFlat(getState());
    await dispatch(
      openCreateAccountForm(
        panelId,
        source,
        cardId,
        accountsCustomFields.map((field) => ({id: field.id, value: ''})),
      ),
    );
    // If we have a name or domain, we should dispatch the actions to add them
    batch(() => {
      if (name) {
        dispatch(createAccountFormUpdateNameAction(panelId, name));
      }
      if (domain) {
        dispatch(
          createAccountFormUpdateDomainsAction(panelId, [
            {
              name: domain,
              validityStatus: DomainValidityStatusesEnum.TO_BE_CHECKED,
            },
          ]),
        );
      }
    });
  };
}

export function deleteCardAndCloseContactDetailsAsyncAction(
  panelId: ContactDetailsPanelId,
  cardId: number,
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const panel = getPanels(getState())[panelId];
    if (!panel || !panelIsContactDetailsPanel(panel)) {
      return;
    }

    try {
      batch(async () => {
        await dispatch(deleteCardsAsyncAction([cardId]));
        dispatch(removePanelAction(panelId));
      });
    } catch (error) {
      dispatch(showContactDetailsErrorBannerAction(panelId, ContactDetailsErrorTypesEnum.DELETION_FAILED));
    }
  };
}

export function contactDetailsValidateDomainAsyncAction(panelId: ContactDetailsPanelId): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const panel = getPanels(getState())[panelId];
    if (!panelIsContactDetailsPanel(panel) || !panel.selectedDomain) {
      return;
    }

    const validatedDomain = await dispatch(validateDomainAsyncAction(panel.selectedDomain));
    dispatch(accountDomainValidatedAction(panelId, validatedDomain));
  };
}

export function contactDetailsAddCardToAccountAsyncAction(
  panelId: ContactDetailsPanelId,
  accountId: number,
  card?: CardModel,
  contact?: VisualContactModel,
): ThunkAsyncAction {
  return async (dispatch) => {
    if (!contact) {
      return;
    }

    // If we do not have a card, we need to create a card first.
    const selectedCard = card ?? (await dispatch(createCardPayloadAction(panelId, contact)));
    // TODO: Show an error to the user that we failed to create/find a card.
    if (!selectedCard) {
      return;
    }

    batch(() => {
      dispatch(
        updateCardAttributeAsyncAction({
          card: selectedCard,
          action: addAccount(accountId),
          shouldShowUndoBanner: true,
          panelId,
        }),
      );
      dispatch(contactDetailsOpenAccountDetailsAction(panelId, accountId));
    });
  };
}

export function contactDetailsOpenCreateNewAccountFormAsyncAction(
  panelId: ContactDetailsPanelId,
  searchText: string,
  card?: CardModel,
  contact?: VisualContactModel,
): ThunkAsyncAction {
  return async (dispatch) => {
    if (!contact) {
      return;
    }

    // If we do not have a card, we need to create a card first.
    const selectedCard = card ?? (await dispatch(createCardPayloadAction(panelId, contact)));
    // TODO: Show an error to the user that we failed to create/find a card.
    if (!selectedCard) {
      return;
    }

    const domain = parseDomainName(searchText) ?? undefined;
    dispatch(
      contactDetailsCreateAccountFormAction(
        panelId,
        ContactDetailsCreateAccountSourceActionsEnum.CONTACT_DETAILS,
        selectedCard.id,
        searchText.split('.')[0],
        domain,
      ),
    );
  };
}

export function getOrFetchCardAsyncAction(cardId: number): ThunkAsyncAction<CardModel | undefined> {
  return async (dispatch, getState) => {
    const cards = getCards(getState());
    if (!cards) {
      return undefined;
    }
    const card = cards[cardId];
    if (card) {
      return card;
    }
    return dispatch(fetchCardAsyncAction(cardId));
  };
}

/*
 * Helpers.
 */

function extractAccountDomainFromCard(contactUid: string, card?: CardModel): DomainModel | undefined {
  // If we do not have a card, we will look at the contact uid and try and pull a custom domain off that.
  if (!card) {
    const contactDomains = extractNonGenericAccountDomainsFromContactUids([contactUid]);
    if (contactDomains.length === 0) {
      return undefined;
    }
    return {
      name: contactDomains[0],
      validityStatus: DomainValidityStatusesEnum.TO_BE_CHECKED,
    };
  }
  // If we already have an account, we don't need to check the domain.
  if (card.accountId) {
    return undefined;
  }

  // Extract the domains from the contact uids on the card and use the first one.
  const domains = extractNonGenericAccountDomainsFromContactUids(card.contactUids);
  if (domains.length === 0) {
    return undefined;
  }
  return {
    name: domains[0],
    validityStatus: DomainValidityStatusesEnum.TO_BE_CHECKED,
  };
}

function createCardPayloadAction(panelId: ContactDetailsPanelId, contact: VisualContactModel) {
  return createCardAndUpdateContactDetailsPanelAction(
    panelId,
    {
      namespace: globalNamespace,
      name: contact.name || contact.contact.handle,
      contacts: [contact.contact],
      type: CardTypesEnum.AUTO,
    },
    true,
  );
}

/*
 * Union type.
 */

export type ContactDetailsPanelAction =
  | FetchContactDetailsDataAction
  | FetchContactDetailsDataCompletedAction
  | FetchContactDetailsDataFailedAction
  | ContactDetailsMergePreviewStartedAction
  | ContactDetailsMergePreviewCompletedAction
  | ContactDetailsMergeFailedAction
  | ContactDetailsMergeSuccessAction
  | ContactDetailsClearMergeAction
  | ContactDetailsCreateFailedAction
  | UpdateContactDetailsPanelSelectedContactAction
  | FetchContactDetailsCardNotesAction
  | FetchContactDetailsCardNotesCompletedAction
  | FetchContactDetailsCardNotesFailedAction
  | FetchContactDetailsConversationsAction
  | FetchContactDetailsConversationsCompletedAction
  | FetchContactDetailsConversationsFailedAction
  | AccountDomainValidatedAction
  | OpenAccountDetailsAction
  | OpenContactDetailsAction
  | OpenCreateAccountFormAction
  | ScrolledToSectionAction
  | ScrollToSectionAction;
